import React, { useEffect, useRef, useState } from 'react';
import { IonIcon, useIonRouter } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { TourProvider, Tour, components, useTour } from '@reactour/tour';
import { triangle, closeOutline } from 'ionicons/icons';
import { useGuidedTour } from '@/hooks/useGuidedTour';
import { useProfileStore } from '@/store/useProfileStore';
import { profileService } from '@/services/profileService';
import { additionalContentService } from '@/services/additionalContentService';
import { badgeModalService } from '@/services/badgeModalService';
import { progressService } from '@/services/progressService';

const TOUR_ACCENT = '#D44D5C';

function TourClose(
  props: React.ComponentProps<typeof components.Close>
) {
  const { styles, disabled, onClick, ...rest } = props;
  return (
    <button
      type="button"
      className="reactour__close-button"
      onClick={onClick}
      disabled={disabled}
      style={{
        position: 'absolute',
        top: 14,
        right: 24,
        minWidth: 44,
        minHeight: 44,
        width: 44,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 0,
        background: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      {...rest}
    >
      <IonIcon
        icon={closeOutline}
        style={{
          width: 22,
          height: 22,
          color: disabled ? 'var(--ion-color-medium)' : '#5e5e5e',
        }}
      />
    </button>
  );
}

function TourArrow({
  inverted,
  disabled,
}: {
  inverted?: boolean;
  disabled?: boolean;
}) {
  return (
    <IonIcon
      icon={triangle}
      style={{
        width: 20,
        height: 20,
        color: disabled ? 'var(--ion-color-medium)' : TOUR_ACCENT,
        transform: inverted ? 'rotate(90deg)' : 'rotate(-90deg)',
      }}
    />
  );
}

const doNotShowRef = { current: false };
const RESIZE_DEBOUNCE_MS = 120;

const PAGE_TO_PATH: Record<string, string> = {
  dashboard: '/dashboard',
  'my-team': '/my-team',
  'mystery-shop': '/mystery-shop',
  'all-courses': '/all-courses',
  'my-progress': '/my-progress',
  'course/welcome': '/course/introduction-video',
};

function TourResizeSync() {
  const { isOpen } = useTour();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen]);
  return null;
}

function TourContentWithCheckbox(props: React.ComponentProps<typeof components.Content>) {
  const [doNotShow, setDoNotShow] = useState(false);
  const { currentStep, steps } = useTour();
  const isFirstStep = currentStep === 0;
  const isLastStep = steps?.length ? currentStep >= steps.length - 1 : false;
  const showDoNotShow = isFirstStep || isLastStep;
  useEffect(() => {
    doNotShowRef.current = doNotShow;
  }, [doNotShow]);
  return (
    <div className="flex flex-col gap-3">
      {showDoNotShow && (
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={doNotShow}
            onChange={(e) => setDoNotShow(e.target.checked)}
            className="w-4 h-4 text-[#D44D5C] border-gray-300 rounded focus:ring-[#D44D5C]"
          />
          <span>Don&apos;t show this walkthrough again</span>
        </label>
      )}
      <components.Content {...props} />
    </div>
  );
}

interface GuidedTourProps {
  children: React.ReactNode;
  page?: string;
  /** When set, fetch steps from all pages and navigate when next/prev step is on another page */
  pages?: string[];
  enabled?: boolean;
  /** Only auto-start when pathname matches (e.g. '/dashboard') */
  autoStartOnlyWhenPath?: string;
}

function TourPendingStepHandler({
  pendingStepIndexRef,
  stepsPageByIndex,
}: {
  pendingStepIndexRef: React.MutableRefObject<number | null>;
  stepsPageByIndex: string[];
}) {
  const location = useLocation();
  const { setCurrentStep } = useTour();

  useEffect(() => {
    const pending = pendingStepIndexRef.current;
    if (pending == null || pending < 0 || pending >= stepsPageByIndex.length) return;
    const pageName = stepsPageByIndex[pending];
    const targetPath = PAGE_TO_PATH[pageName];
    if (targetPath && location.pathname === targetPath) {
      pendingStepIndexRef.current = null;
      const stepToSet = pending;
      let cancelled = false;
      const rafId = requestAnimationFrame(() => {
        if (cancelled) return;
        setCurrentStep(stepToSet);
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
      };
    }
  }, [location.pathname, stepsPageByIndex, pendingStepIndexRef, setCurrentStep]);

  return null;
}

export const GuidedTour: React.FC<GuidedTourProps> = ({
  children,
  page = 'dashboard',
  pages,
  enabled = true,
  autoStartOnlyWhenPath = '/dashboard',
}) => {
  const location = useLocation();
  const router = useIonRouter();
  const activeProfile = useProfileStore((state) => state.activeProfile);
  const setActiveProfile = useProfileStore((state) => state.setActiveProfile);
  const pendingStepIndexRef = useRef<number | null>(null);
  const { steps, loading, shouldAutoStart, hasSteps, stepsPageByIndex = [], tourCompletionItem, walkthroughSlot = 'walkthrough' } =
    useGuidedTour({ page, pages, enabled });

  const canAutoStart =
    shouldAutoStart &&
    (!autoStartOnlyWhenPath || location.pathname === autoStartOnlyWhenPath);

  const [autoStartKey, setAutoStartKey] = useState(0);
  useEffect(() => {
    if (!canAutoStart || !hasSteps || autoStartKey !== 0) return;
    const openTour = () => setAutoStartKey(1);
    let rafId: number;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        timeoutId = setTimeout(openTour, 100);
      });
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [canAutoStart, hasSteps, autoStartKey]);

  const handleAfterOpen = () => {
    const fire = () => window.dispatchEvent(new Event('resize'));
    fire();
    setTimeout(fire, 50);
  };

  const handleBeforeClose = async () => {
    if (!doNotShowRef.current || !activeProfile) return;
    const currentMetadata = activeProfile.metadata || {};
    if (currentMetadata.doNotShow?.[walkthroughSlot as keyof typeof currentMetadata.doNotShow]) return;
    const updatedMetadata = {
      ...currentMetadata,
      settings: currentMetadata.settings || {},
      doNotShow: { ...currentMetadata.doNotShow, [walkthroughSlot]: true },
    };
    try {
      const savedProfile = await profileService.patch({
        id: activeProfile.id,
        metadata: updatedMetadata,
      });
      setActiveProfile({
        ...savedProfile,
        metadata: {
          ...savedProfile.metadata,
          doNotShow: { ...savedProfile.metadata?.doNotShow, ...updatedMetadata.doNotShow },
        },
      });
    } catch {
      setActiveProfile({ ...activeProfile, metadata: updatedMetadata });
    }
  };

  const handleCompleteWalkthrough = async (
    setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void
  ) => {
    try {
      // Badge-on-walkthrough-completion: commented out per product decision
      // if (tourCompletionItem) {
      //   const response = await additionalContentService.completeAdditionalContent(
      //     tourCompletionItem.id,
      //     100,
      //     activeProfile?.id
      //   );
      //   if (response.badgesEarned?.length) {
      //     response.badgesEarned.forEach((badge) =>
      //       badgeModalService.show(badge, activeProfile?.id)
      //     );
      //   } else if (activeProfile?.id) {
      //     progressService
      //       .getTeamUserSummary(activeProfile.id)
      //       .then((summary) => {
      //         if (summary.badges?.length) {
      //           badgeModalService.checkAndShowNewBadges(summary.badges, activeProfile!.id);
      //         }
      //       })
      //       .catch(() => {});
      //   }
      // }
    } catch {
      // Silent failure; still close tour
    } finally {
      setIsOpen(false);
    }
  };

  if (!enabled || (!loading && !hasSteps)) {
    return <>{children}</>;
  }

  const tourStyles = {
    button: (base: React.CSSProperties) => ({
      ...base,
      minWidth: 44,
      minHeight: 44,
      padding: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      touchAction: 'manipulation',
    }),
    clickArea: (base: React.CSSProperties) => ({
      ...base,
      pointerEvents: 'none' as const,
    }),
  };

  const makeNavButton = (kind: 'prev' | 'next') => {
    return function TourNavButton({
      Button,
      setCurrentStep,
      currentStep,
      stepsLength,
      setIsOpen,
    }: {
      Button: React.FC<{ kind?: 'prev' | 'next' }>;
      setCurrentStep: (n: number | ((prev: number) => number)) => void;
      currentStep: number;
      stepsLength: number;
      setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
    }) {
      const isLastStep = currentStep >= stepsLength - 1;
      const disabled =
        kind === 'prev' ? currentStep <= 0 : !isLastStep && currentStep >= stepsLength - 1;
      const go = () => {
        if (kind === 'next' && isLastStep) {
          handleCompleteWalkthrough(setIsOpen);
          return;
        }
        if (disabled) return;
        const nextIndex = kind === 'prev' ? currentStep - 1 : currentStep + 1;
        if (stepsPageByIndex.length > 0 && nextIndex >= 0 && nextIndex < stepsPageByIndex.length) {
          const stepPage = stepsPageByIndex[nextIndex];
          const targetPath = PAGE_TO_PATH[stepPage];
          if (targetPath && targetPath !== location.pathname) {
            pendingStepIndexRef.current = nextIndex;
            router.push(targetPath);
            return;
          }
        }
        setCurrentStep((i) =>
          kind === 'prev' ? Math.max(0, i - 1) : Math.min(stepsLength - 1, i + 1)
        );
      };
      const isCompleteButton = kind === 'next' && isLastStep;
      return (
        <div
          role="button"
          tabIndex={0}
          aria-label={isCompleteButton ? 'Complete walkthrough' : `Go to ${kind} step`}
          onPointerDown={(e) => {
            e.preventDefault();
            go();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              go();
            }
          }}
          style={{
            display: 'inline-flex',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          {isCompleteButton ? (
            <span
              className="text-sm font-medium px-3 py-2 rounded-lg transition-colors"
              style={{ color: TOUR_ACCENT }}
            >
              Complete Walkthrough
            </span>
          ) : (
            <Button kind={kind} />
          )}
        </div>
      );
    };
  };

  return (
    <TourProvider
      key={autoStartKey}
      steps={steps}
      defaultOpen={autoStartKey > 0}
      showCloseButton
      showBadge
      showNavigation
      styles={tourStyles}
      afterOpen={handleAfterOpen}
      beforeClose={handleBeforeClose}
      components={{ Content: TourContentWithCheckbox, Arrow: TourArrow, Close: TourClose }}
      prevButton={makeNavButton('prev')}
      nextButton={makeNavButton('next')}
    >
      {children}
      <TourPendingStepHandler
        pendingStepIndexRef={pendingStepIndexRef}
        stepsPageByIndex={stepsPageByIndex}
      />
      <TourResizeSync />
      <Tour {...({} as React.ComponentProps<typeof Tour>)} />
    </TourProvider>
  );
};
